import type { ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { usePreferences } from '@/stores/preferences-store';
import { formatDateTime, formatMoney } from '@/utils/format';

export interface PaymentIntentDetail {
  id: string;
  method: 'mobile_money' | 'card' | null;
  amountMinor: number;
  currency: string;
  status:
    | 'created'
    | 'pending'
    | 'succeeded'
    | 'failed'
    | 'cancelled'
    | 'expired'
    | 'review_required';
  failureCode: string | null;
  contributionId: string | null;
  isTest: boolean;
  giverName: string | null;
  fundId: string;
  fundName: string | null;
  reference: string;
  providerReference: string | null;
  externalReference: string | null;
  mobileMoneyNetwork: string | null;
  feeMinor: number | null;
  confirmedAmountMinor: number | null;
  completedAt: string | null;
  createdAt: string;
}

type ProviderCheckStatus =
  | PaymentIntentDetail['status']
  | 'pending'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'expired'
  | 'refunded';

interface PaymentIntentRecheckResult {
  intent: PaymentIntentDetail;
  providerCheck: {
    status: ProviderCheckStatus;
    confirmedAmountMinor: number | null;
    currency: string | null;
    method: PaymentIntentDetail['method'];
    channel: string | null;
    externalReference: string | null;
    feeMinor: number | null;
    providerReference: string | null;
    credentialSource: 'frozen' | 'current';
    failureCode: string | null;
    failureReason: string | null;
    providerStatusLabel: string | null;
    processorResult: 'successful' | 'pending' | 'failed' | 'expired' | 'cancelled' | 'refunded';
  };
  outcome:
    | 'contribution_created'
    | 'status_updated'
    | 'unchanged'
    | 'verified'
    | 'provider_pending'
    | 'provider_failed'
    | 'review_required';
}

const statusBadgeVariant = (status: PaymentIntentDetail['status']): BadgeVariant => {
  if (status === 'succeeded') return 'success';
  if (status === 'failed' || status === 'cancelled' || status === 'expired') return 'danger';
  if (status === 'review_required') return 'warning';
  return 'info';
};

const providerStatusBadgeVariant = (status: ProviderCheckStatus): BadgeVariant => {
  if (status === 'succeeded') return 'success';
  if (status === 'failed' || status === 'cancelled' || status === 'expired' || status === 'refunded') {
    return 'danger';
  }
  return 'info';
};

const providerStatusKey = (status: ProviderCheckStatus): string => {
  if (
    status === 'created' ||
    status === 'pending' ||
    status === 'succeeded' ||
    status === 'failed' ||
    status === 'cancelled' ||
    status === 'expired' ||
    status === 'review_required'
  ) {
    return `attempts.status.${status}`;
  }
  return 'attempts.recheck.providerStatusUnknown';
};

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid gap-1 border-b border-border py-3 last:border-b-0 sm:grid-cols-[9rem_1fr] sm:gap-4">
    <dt className="text-body-sm text-fg-secondary">{label}</dt>
    <dd className="text-body text-fg">{children}</dd>
  </div>
);

const formatMethod = (
  method: PaymentIntentDetail['method'],
  channel: string | null | undefined,
  t: (key: string) => string,
): string => {
  if (!method) return '—';
  const label = t(`attempts.method.${method}`);
  return channel?.trim() ? `${label} · ${channel.trim()}` : label;
};

const resolveFailureMessage = (
  failureCode: string | null,
  failureReason: string | null,
  t: (key: string, options?: { defaultValue?: string }) => string,
  tErrors: (key: string) => string,
): string | null => {
  if (failureReason) {
    const reasonKey = `attempts.providerReasons.${failureReason}`;
    const reasonLabel = t(reasonKey, { defaultValue: '' });
    if (reasonLabel) return reasonLabel;
  }
  if (!failureCode) return null;
  return tErrors(failureCode === 'UNKNOWN' ? 'PAYMENT_FAILED_UNKNOWN' : failureCode);
};

export const PaymentIntentDetailDialog = ({
  intent,
  open,
  onOpenChange,
  canRecheck = false,
  canReconcile = false,
  canRecordGift = false,
  onIntentUpdated,
  onRecheckSuccess,
}: {
  intent: PaymentIntentDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canRecheck?: boolean;
  canReconcile?: boolean;
  canRecordGift?: boolean;
  onIntentUpdated?: (intent: PaymentIntentDetail) => void;
  onRecheckSuccess?: () => void;
}) => {
  const { t } = useTranslation('payments');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const locale = usePreferences((state) => state.locale);
  const timezone = usePreferences((state) => state.timezone);
  const [displayIntent, setDisplayIntent] = useState<PaymentIntentDetail | null>(intent);
  const [providerCheck, setProviderCheck] = useState<PaymentIntentRecheckResult['providerCheck'] | null>(
    null,
  );
  const [recheckOutcome, setRecheckOutcome] = useState<PaymentIntentRecheckResult['outcome'] | null>(
    null,
  );
  const autoRecheckIntentId = useRef<string | null>(null);
  const previousIntentIdRef = useRef<string | null>(null);
  const isManualRecheckRef = useRef(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  useEffect(() => {
    const nextId = intent?.id ?? null;
    if (nextId !== previousIntentIdRef.current) {
      previousIntentIdRef.current = nextId;
      setDisplayIntent(intent);
      setProviderCheck(null);
      setRecheckOutcome(null);
      autoRecheckIntentId.current = null;
      return;
    }
    if (intent) {
      setDisplayIntent(intent);
    }
  }, [intent]);

  const recheck = useMutation({
    mutationFn: (intentId: string) =>
      api.post<PaymentIntentRecheckResult>(`/payments/intents/${intentId}/recheck`),
    onSuccess: (result) => {
      setDisplayIntent(result.intent);
      setProviderCheck(result.providerCheck);
      setRecheckOutcome(result.outcome);
      onIntentUpdated?.(result.intent);

      const stillPending =
        result.outcome === 'provider_pending' ||
        (result.outcome === 'unchanged' && result.providerCheck.processorResult === 'pending');
      if (!stillPending) {
        onRecheckSuccess?.();
      }

      const shouldToast =
        isManualRecheckRef.current ||
        result.outcome === 'contribution_created' ||
        result.outcome === 'status_updated' ||
        result.outcome === 'review_required' ||
        result.outcome === 'provider_failed' ||
        (result.outcome === 'unchanged' && result.providerCheck.processorResult === 'successful') ||
        result.outcome === 'verified';
      if (shouldToast) {
        const toastKey =
          result.outcome === 'unchanged' && result.providerCheck.processorResult === 'successful'
            ? 'verified'
            : result.outcome;
        toast.success(t(`attempts.recheck.outcome.${toastKey}`));
      }
      isManualRecheckRef.current = false;
    },
    onError: (error) => {
      isManualRecheckRef.current = false;
      const message =
        error instanceof ApiError
          ? tErrors(error.code, { defaultValue: t('attempts.recheck.error') })
          : t('attempts.recheck.error');
      toast.error(message);
    },
  });

  const resolveReview = useMutation({
    mutationFn: (input: { intentId: string; resolution: 'record' | 'reject' }) =>
      api.post<{ intent: PaymentIntentDetail; contributionId: string | null }>(
        `/payments/intents/${input.intentId}/resolve-review`,
        { resolution: input.resolution },
      ),
    onSuccess: (result, variables) => {
      setDisplayIntent(result.intent);
      setProviderCheck(null);
      setRecheckOutcome(null);
      onIntentUpdated?.(result.intent);
      onRecheckSuccess?.();
      toast.success(
        t(
          variables.resolution === 'record'
            ? 'attempts.review.recorded'
            : 'attempts.review.rejected',
        ),
      );
      if (variables.resolution === 'reject') {
        setRejectOpen(false);
      }
    },
    onError: (error) => {
      const message =
        error instanceof ApiError
          ? tErrors(error.code, { defaultValue: t('attempts.recheck.error') })
          : t('attempts.recheck.error');
      toast.error(message);
    },
  });

  const activeIntent = displayIntent ?? intent;

  useEffect(() => {
    if (!open || !activeIntent || !canRecheck || recheck.isPending) return;
    if (activeIntent.status === 'succeeded') return;
    if (autoRecheckIntentId.current === activeIntent.id) return;
    autoRecheckIntentId.current = activeIntent.id;
    recheck.mutate(activeIntent.id);
  }, [open, activeIntent?.id, activeIntent?.status, canRecheck, recheck.isPending, recheck.mutate]);

  const when = activeIntent
    ? formatDateTime(activeIntent.completedAt ?? activeIntent.createdAt, { locale, timezone })
    : null;

  const check = providerCheck;
  const methodLabel = formatMethod(
    check?.method ?? activeIntent?.method ?? null,
    check?.channel ?? activeIntent?.mobileMoneyNetwork,
    t,
  );
  const receiptNumber = check?.externalReference ?? activeIntent?.externalReference;
  const providerTransactionId = check?.providerReference ?? activeIntent?.providerReference;
  const confirmedAmountMinor = check?.confirmedAmountMinor ?? activeIntent?.confirmedAmountMinor;
  const feeMinor = check?.feeMinor ?? activeIntent?.feeMinor;
  const currency = check?.currency ?? activeIntent?.currency ?? 'GHS';
  const failureMessage = resolveFailureMessage(
    check?.failureCode ?? activeIntent?.failureCode ?? null,
    check?.failureReason ?? null,
    t,
    tErrors,
  );
  const showReviewActions =
    canReconcile && activeIntent?.status === 'review_required' && !activeIntent.contributionId;
  const recordAmountMinor =
    confirmedAmountMinor ??
    (activeIntent?.confirmedAmountMinor != null ? activeIntent.confirmedAmountMinor : null);
  const canSubmitRecord = showReviewActions && canRecordGift && recordAmountMinor != null;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="md"
        className="overflow-visible sm:max-w-xl"
        closeLabel={tCommon('action.close')}
      >
        <DialogHeader>
          <DialogTitle>
            {when
              ? t('attempts.detail.dialogTitleDated', { date: when })
              : t('attempts.detail.dialogTitle')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {activeIntent ? (
            <dl>
              <DetailRow label={t('attempts.detail.amount')}>
                <span className="font-medium tabular-nums">
                  {formatMoney(activeIntent.amountMinor, activeIntent.currency, { locale })}
                </span>
              </DetailRow>
              <DetailRow label={t('attempts.columns.fund')}>
                {activeIntent.fundName?.trim() || '—'}
              </DetailRow>
              <DetailRow label={t('attempts.columns.status')}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={statusBadgeVariant(activeIntent.status)}>
                    {t(`attempts.status.${activeIntent.status}`)}
                  </Badge>
                  {activeIntent.isTest ? (
                    <Badge variant="neutral">{t('attempts.testBadge')}</Badge>
                  ) : null}
                </div>
              </DetailRow>
              <DetailRow label={t('attempts.columns.giver')}>
                {activeIntent.giverName?.trim() || '—'}
              </DetailRow>
              <DetailRow label={t('attempts.detail.reference')}>
                <span className="break-all font-mono text-body-sm">{activeIntent.reference}</span>
              </DetailRow>
              <DetailRow label={t('attempts.columns.date')}>{when ?? '—'}</DetailRow>

              <div className="pt-4">
                <p className="text-body-sm font-medium text-fg">
                  {t('attempts.recheck.confirmationSection')}
                </p>
              </div>

              {recheck.isPending && !check ? (
                <p className="py-3 text-body-sm text-fg-secondary">{t('attempts.recheck.checking')}</p>
              ) : null}

              {check || receiptNumber || providerTransactionId || methodLabel !== '—' ? (
                <>
                  {check ? (
                    <DetailRow label={t('attempts.recheck.providerStatus')}>
                      <Badge variant={providerStatusBadgeVariant(check.status)}>
                        {t(providerStatusKey(check.status))}
                      </Badge>
                    </DetailRow>
                  ) : null}
                  <DetailRow label={t('attempts.columns.method')}>{methodLabel}</DetailRow>
                  <DetailRow label={t('attempts.recheck.processorResult')}>
                    {check ? (
                      <span
                        className={
                          check.processorResult === 'successful'
                            ? 'font-medium text-success-fg'
                            : check.processorResult === 'pending'
                              ? 'text-fg-secondary'
                              : 'text-danger'
                        }
                      >
                        {t(`attempts.recheck.processorResults.${check.processorResult}`)}
                      </span>
                    ) : failureMessage ? (
                      <span className="text-danger">{failureMessage}</span>
                    ) : activeIntent.contributionId ? (
                      <span className="font-medium text-success-fg">
                        {t('attempts.recheck.processorResults.successful')}
                      </span>
                    ) : (
                      '—'
                    )}
                  </DetailRow>
                  {receiptNumber ? (
                    <DetailRow label={t('attempts.recheck.receiptNumber')}>
                      <span className="break-all font-mono text-body-sm">{receiptNumber}</span>
                    </DetailRow>
                  ) : null}
                  {providerTransactionId ? (
                    <DetailRow label={t('attempts.recheck.providerReference')}>
                      <span className="break-all font-mono text-body-sm">{providerTransactionId}</span>
                    </DetailRow>
                  ) : null}
                  {feeMinor != null ? (
                    <DetailRow label={t('attempts.recheck.providerFee')}>
                      {formatMoney(feeMinor, currency, { locale })}
                    </DetailRow>
                  ) : null}
                  {confirmedAmountMinor != null ? (
                    <DetailRow label={t('attempts.recheck.confirmedAmount')}>
                      {formatMoney(confirmedAmountMinor, currency, { locale })}
                    </DetailRow>
                  ) : null}
                  {failureMessage && check ? (
                    <DetailRow label={t('attempts.recheck.providerResult')}>
                      <span className="text-danger">{failureMessage}</span>
                    </DetailRow>
                  ) : null}
                </>
              ) : (
                <p className="py-3 text-body-sm text-fg-secondary">
                  {canRecheck ? t('attempts.recheck.confirmationEmpty') : '—'}
                </p>
              )}

              <DetailRow label={t('attempts.columns.outcome')}>
                {activeIntent.contributionId ? (
                  <Link
                    to={`/giving/contributions/${activeIntent.contributionId}`}
                    className="font-medium text-brand hover:underline"
                  >
                    {t('attempts.viewContribution')}
                  </Link>
                ) : failureMessage && !check ? (
                  <span className="text-danger">{failureMessage}</span>
                ) : recheckOutcome ? (
                  t(`attempts.recheck.outcome.${recheckOutcome}`)
                ) : activeIntent.status === 'pending' || activeIntent.status === 'created' ? (
                  t('attempts.recheck.outcome.provider_pending')
                ) : (
                  '—'
                )}
              </DetailRow>

              {check ? (
                <>
                  <DetailRow label={t('attempts.recheck.keysUsed')}>
                    {check.credentialSource === 'frozen'
                      ? t('attempts.recheck.credentialFrozen')
                      : t('attempts.recheck.credentialCurrent')}
                  </DetailRow>
                  {recheckOutcome ? (
                    <DetailRow label={t('attempts.recheck.trinityOutcome')}>
                      {t(`attempts.recheck.outcome.${recheckOutcome}`)}
                    </DetailRow>
                  ) : null}
                </>
              ) : null}
            </dl>
          ) : null}

          {showReviewActions ? (
            <div className="mt-6 rounded-lg border border-warning/30 bg-warning/5 p-4">
              <p className="text-body-sm font-medium text-fg">{t('attempts.review.sectionTitle')}</p>
              <p className="mt-2 text-body-sm text-fg-secondary">{t('attempts.review.body')}</p>
              {recordAmountMinor != null ? (
                <p className="mt-3 text-body-sm text-fg">
                  {t('attempts.review.mismatch', {
                    requested: formatMoney(activeIntent!.amountMinor, currency, { locale }),
                    confirmed: formatMoney(recordAmountMinor, currency, { locale }),
                  })}
                </p>
              ) : null}
            </div>
          ) : null}
        </DialogBody>
        <DialogFooter className="w-full min-w-0 !flex-col gap-2 sm:!flex-row sm:flex-wrap sm:justify-end">
          {canRecheck && activeIntent ? (
            <Button
              variant="secondary"
              className="w-full sm:w-auto"
              disabled={recheck.isPending}
              onClick={() => {
                isManualRecheckRef.current = true;
                recheck.mutate(activeIntent.id);
              }}
            >
              {recheck.isPending ? t('attempts.recheck.checking') : t('attempts.recheck.action')}
            </Button>
          ) : null}
          {showReviewActions ? (
            <Button
              variant="secondary"
              className="w-full sm:w-auto"
              disabled={resolveReview.isPending}
              onClick={() => setRejectOpen(true)}
            >
              {t('attempts.review.rejectGift')}
            </Button>
          ) : null}
          {canSubmitRecord ? (
            <Button
              className="w-full border border-transparent shadow-none sm:w-auto"
              disabled={resolveReview.isPending}
              onClick={() =>
                resolveReview.mutate({
                  intentId: activeIntent!.id,
                  resolution: 'record',
                })
              }
            >
              {t('attempts.review.recordGift')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <ConfirmDialog
      open={rejectOpen}
      onOpenChange={setRejectOpen}
      title={t('attempts.review.rejectTitle')}
      description={t('attempts.review.rejectDescription')}
      confirmLabel={t('attempts.review.rejectConfirm')}
      cancelLabel={tCommon('action.cancel')}
      destructive
      loading={resolveReview.isPending}
      onConfirm={async () => {
        if (!activeIntent) return;
        await resolveReview.mutateAsync({ intentId: activeIntent.id, resolution: 'reject' });
      }}
    />
    </>
  );
};
